/**
 * Membership Stats month grid (WSRADM-0021 / WSRADM-0035).
 *
 * Duration columns come from the envelope `durationColumns`, never from row 1's
 * bands. `coveredMembers` / `netNewMembers` are rendered as sent.
 * Hidden columns live in `?hide=` + the `wsr_admin_views` cookie (WSRADM-0051).
 */
import { useState } from 'react';
import { Alert, Snackbar, Stack, Typography } from '@mui/material';
import { fetchMembershipMonths, rebuildReports } from '@/api/reports/reportsApi';
import { useAuth } from '@/auth/AuthContext';
import { canRebuildReports } from '@/auth/reportAccess';
import { ColumnChooser, HiddenHint } from '@/components/ColumnChooser';
import { CompareToggle, useComparePref } from '@/components/CompareSupport';
import { MembershipMonthsTable } from '@/components/MembershipMonthsTable';
import { ReportFreshness } from '@/components/ReportFreshness';
import { ErrorDetails } from '@/components/ErrorDetails';
import { useCopy } from '@/hooks/useCopy';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { MEMBERSHIP_FIXED_KEYS, bandViewKey } from '@/config/viewPrefs';
import { useHiddenKeys } from '@/hooks/useHiddenKeys';
import { useResource } from '@/hooks/useResource';
import { MembershipByFamilySection } from './MembershipByFamilySection';

const PAGE_SIZE_DEFAULT = 24;

export function MembershipStatsPage() {
  const { t } = useCopy();
  const { operator } = useAuth();
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(PAGE_SIZE_DEFAULT);
  const [rebuilding, setRebuilding] = useState(false);
  const [rebuildNotice, setRebuildNotice] = useState<'ok' | 'error' | null>(null);
  const [rebuildError, setRebuildError] = useState<unknown>(null);
  const [compareMode, setCompareMode] = useComparePref('membershipCompare');
  // Keep the last notice kind so the snackbar content does not flip to the success
  // copy while an error is transitioning out after being dismissed.
  const [lastNotice, setLastNotice] = useState<'ok' | 'error'>('ok');
  if (rebuildNotice !== null && rebuildNotice !== lastNotice) setLastNotice(rebuildNotice);
  const shownNotice = rebuildNotice ?? lastNotice;

  const resource = useResource(
    () => fetchMembershipMonths({ from: '', to: '', page, size }),
    [page, size],
  );

  const durationColumns = resource.data?.durationColumns ?? [];
  const membersGroup = t('views.groupMembers', 'Members');
  const durationGroup = t('views.groupDuration', 'Duration');
  const fixedLabels: Record<(typeof MEMBERSHIP_FIXED_KEYS)[number], string> = {
    lost: t('reports.membership.columns.lost', 'Lost Members'),
    lapsed: t('reports.membership.columns.lapsed', 'Lapsed (in grace)'),
    netMembers: t('reports.membership.columns.netMembers', 'Net Members'),
    netNew: t('reports.membership.columns.netNew', 'Net New Members'),
    existing: t('reports.membership.columns.existing', 'Existing Members'),
  };
  const columnOptions = [
    ...MEMBERSHIP_FIXED_KEYS.map((key) => ({
      key,
      label: fixedLabels[key],
      group: membersGroup,
    })),
    ...durationColumns.map((column) => ({
      key: bandViewKey(column.bandKey),
      label: column.label,
      group: durationGroup,
    })),
  ];
  const views = useHiddenKeys({
    param: 'hide',
    prefsKey: 'membership',
    available: columnOptions.map((option) => option.key),
  });

  const canRebuild = canRebuildReports(operator?.roles);
  const title = t('page.membershipStats.title', 'Membership Stats');
  useDocumentTitle(title);

  const handleRebuild = () => {
    setRebuilding(true);
    void rebuildReports()
      .then(() => setRebuildNotice('ok'))
      .catch((err: unknown) => {
        setRebuildError(err);
        setRebuildNotice('error');
      })
      .finally(() => setRebuilding(false));
  };

  const neverComputed = resource.status === 'ready' && resource.data?.neverComputed === true;

  return (
    <>
      <Typography variant="h1" sx={{ mb: 2.5 }}>
        {title}
      </Typography>

      {resource.data ? (
        <ReportFreshness
          asOf={resource.data.asOf}
          stale={resource.data.stale}
          neverComputed={resource.data.neverComputed}
          timezone={resource.data.timezone}
          canRebuild={canRebuild}
          onRebuild={handleRebuild}
          rebuilding={rebuilding}
        />
      ) : null}

      {resource.status === 'ready' && !neverComputed ? (
        <Stack
          direction="row"
          spacing={2}
          sx={{ mb: 2, flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center' }}
          useFlexGap
        >
          <CompareToggle value={compareMode} onChange={setCompareMode} />
          <ColumnChooser
            options={columnOptions}
            hidden={views.hidden}
            onToggle={views.toggle}
            onShowAll={views.showAll}
          />
        </Stack>
      ) : null}

      {neverComputed ? null : (
        <MembershipMonthsTable
          rows={resource.data?.content ?? []}
          durationColumns={durationColumns}
          hidden={views.hidden}
          compare={compareMode}
          status={resource.status}
          error={resource.error}
          totalElements={resource.data?.totalElements ?? 0}
          page={page}
          size={size}
          onPageChange={setPage}
          onRowsPerPageChange={(next) => {
            setSize(next);
            setPage(0);
          }}
        />
      )}

      {resource.status === 'ready' && !neverComputed ? (
        <HiddenHint count={views.hiddenCount} kind="columns" onShowAll={views.showAll} />
      ) : null}

      <MembershipByFamilySection />

      <Snackbar
        open={rebuildNotice !== null}
        autoHideDuration={rebuildNotice === 'error' ? null : 6000}
        onClose={(_event, reason) => {
          // A failure stays until dismissed — a click elsewhere must not hide it.
          if (reason === 'clickaway' && rebuildNotice === 'error') return;
          setRebuildNotice(null);
        }}
      >
        <Alert
          severity={shownNotice === 'error' ? 'error' : 'success'}
          onClose={() => setRebuildNotice(null)}
        >
          {shownNotice === 'error' ? (
            <>
              {t('reports.freshness.rebuildError', 'Could not start a rebuild.')}
              <ErrorDetails
                error={rebuildError}
                headline={t('reports.freshness.rebuildError', 'Could not start a rebuild.')}
              />
            </>
          ) : (
            t(
              'reports.freshness.rebuildQueued',
              'Rebuild queued. Numbers will refresh when the rollup finishes.',
            )
          )}
        </Alert>
      </Snackbar>
    </>
  );
}
